import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import type { UseMutationOptions, UseMutationResult } from 'react-query';

// Price-matrix create path: the BE resolves the supplier product by
// (supplierId, masterProductId), creates it from the master product when the
// supplier doesn't carry it yet, and upserts the deal — so an empty matrix
// cell needs exactly one call regardless of the supplier's catalog state.
const endpoint = 'api/v1/cms/exclusive-deals/from-master';
export const createDealFromMasterEndpointIdentifier = endpoint;

export interface CreateDealFromMasterBody {
  supplierId: number;
  masterProductId: number;
  dealPrice: number;
  // Ladder rung the price is agreed against; the BE defaults to the master's
  // outermost defined rung when absent.
  dealUnit?: 'PACK' | 'ITEM' | 'BASE';
}

type TData = Record<string, any>;
type TError = AxiosError;
type TVariables = { body: CreateDealFromMasterBody };
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useCreateDealFromMasterApi(options?: Options): ReturnType {
  const toast = useCustomToast();

  const mutationFn = async ({ body }: TVariables) => {
    try {
      const { data } = await http.post(endpoint, body);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [endpoint],
    onSuccess: () => {
      toast({ description: 'Price has been recorded successfully', status: 'success' });
    },
    ...options,
  });
}
